/**
 * Client d'authentification.
 *
 * Aucun jeton ne transite ici : le serveur pose un cookie `HttpOnly`, invisible au
 * JavaScript, et le navigateur le renvoie seul. C'est délibéré — un jeton lisible par un
 * script serait volable par une seule faille XSS, et l'écran de connexion est justement
 * la page où un script injecté ferait le plus de dégâts.
 *
 * Conséquence pratique : ce module n'a rien à stocker. « Suis-je connecté ? » n'a qu'une
 * réponse valable, celle du serveur, obtenue par `me()`.
 */

import { apiFetch, detailOf } from "./http";

export type Role = "admin" | "engineer" | "technician";

export interface Identity {
  user_id: string;
  email: string;
  org_id: string | null;
  role: Role | null;
  is_staff: boolean;
}

/** Libellés des rôles, tels qu'ils apparaissent dans l'interface. */
export const ROLE_LABEL: Record<Role, string> = {
  admin: "Administrateur",
  engineer: "Ingénieur",
  technician: "Technicien",
};

/** Ce que chaque rôle a le droit de faire, en une ligne compréhensible. */
export const ROLE_HINT: Record<Role, string> = {
  admin: "Configure l'organisation, les sources de données et les membres",
  engineer: "Dépose des fichiers, ajuste les limites, saisit les tolérances",
  technician: "Consulte les résultats et navigue — ne modifie rien",
};

/** Longueur minimale du mot de passe, alignée sur la vérification du serveur. */
export const MIN_PASSWORD_LENGTH = 12;

/**
 * Erreur d'authentification portant assez d'information pour être affichée telle quelle.
 *
 * `retryAfter` n'est renseigné que sur un 429 : après plusieurs échecs, le serveur
 * temporise. Annoncer le délai vaut mieux que laisser croire à une panne — quelqu'un qui
 * ignore qu'il doit attendre trente secondes réessaie dix fois et aggrave son cas.
 */
export class AuthError extends Error {
  readonly status: number;
  readonly retryAfter: number | null;

  constructor(message: string, status: number, retryAfter: number | null) {
    super(message);
    this.name = "AuthError";
    this.status = status;
    this.retryAfter = retryAfter;
  }
}

async function fail(response: Response): Promise<never> {
  const entete = response.headers.get("retry-after");
  const delai = entete ? Number.parseInt(entete, 10) : Number.NaN;
  throw new AuthError(
    await detailOf(response),
    response.status,
    Number.isFinite(delai) ? delai : null,
  );
}

/**
 * Identité courante, ou `null` si personne n'est connecté.
 *
 * Le 401 est une réponse normale ici, pas une erreur : c'est la question posée. On
 * contourne donc `apiFetch`, dont le rôle est d'annoncer les pertes de session — un
 * visiteur qui n'a jamais eu de session n'en a pas perdu.
 */
export async function me(): Promise<Identity | null> {
  const response = await fetch("/v1/auth/me", { credentials: "same-origin" });
  if (response.status === 401) return null;
  if (!response.ok) await fail(response);
  return (await response.json()) as Identity;
}

export async function login(
  email: string,
  password: string,
): Promise<Identity> {
  const response = await fetch("/v1/auth/login", {
    method: "POST",
    credentials: "same-origin",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  if (!response.ok) await fail(response);
  return (await response.json()) as Identity;
}

export async function register(input: {
  organization: string;
  email: string;
  password: string;
  fullName?: string;
}): Promise<Identity> {
  const response = await fetch("/v1/auth/register", {
    method: "POST",
    credentials: "same-origin",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      organization: input.organization,
      email: input.email,
      password: input.password,
      full_name: input.fullName?.trim() || null,
    }),
  });
  if (!response.ok) await fail(response);
  return (await response.json()) as Identity;
}

/**
 * Ferme la session serveur.
 *
 * Une erreur n'est pas remontée : l'utilisateur a demandé à sortir, et lui refuser la
 * sortie parce que le réseau a hoqueté serait exactement l'inverse du service attendu.
 * Le serveur répond d'ailleurs 204 même sans session, pour la même raison.
 */
export async function logout(): Promise<void> {
  await apiFetch("/v1/auth/logout", { method: "POST" }).catch(() => undefined);
}
